// NotificationsPopover — top-bar bell with a popover panel.
//
// Surfaces three categories from existing tables (no new schema):
//   1. Pending approvals     (table: approvals)
//   2. Service issues        (cached heartbeats; warning + alert tiers)
//   3. Latest Brain digest   (table: autonomous_brain_log; top 1)
//
// Badge dot rule:
//   The copper dot shows iff pending-approvals-count + warning-or-worse
//   daemon count > 0. When everything is clean, the dot hides — the
//   bell ceases to lie. Previous version always showed the dot, which
//   meant operators learned to ignore it.
//
// Cache reuse:
//   Heartbeats come from the SHARED ['heartbeats'] React Query cache
//   populated by SystemStatusChip + Settings + Agents. We never fire a
//   refetch from this component.
//
// Polling:
//   Approvals: 10s (matches Inbox.tsx cadence — that's the page they'll
//   land on after clicking).
//   Latest digest: 60s (Brain runs nightly; faster polling is wasted
//   work).
//
// Screen field audit (CLAUDE.md §19):
//   Approval row title    ← `emails.subject || draft_payload.subject`
//   Approval row meta     ← `formatDistanceToNowStrict(emails.occurred_at || approvals.created_at)`
//   Service row title     ← humanized daemon_name + status label
//   Digest row title      ← "Last digest" label
//   Digest row body       ← `autonomous_brain_log.summary` (first line)

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import { AlertCircle, Bell, Lightbulb, Mail, type LucideIcon } from 'lucide-react';
import {
  classifyHeartbeat,
  type HeartbeatRow,
  type HeartbeatStatus,
} from '@reverra/shared/heartbeat/read';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { supabase } from '@/lib/supabase';
import { humanizeDaemonName } from '@/utils/humanizeDaemonName';

// ============================================================================
// Approvals query
// ============================================================================

type BellApproval = {
  id: string;
  subject: string;
  fromLine: string;
  occurredAt: string;
};

const BELL_APPROVAL_SELECT =
  'id, created_at, draft_payload, emails:target_email_id (from_email, from_name, subject, occurred_at)';

async function fetchPendingApprovalsForBell(): Promise<BellApproval[]> {
  // Phase 1: email is the only channel with an approval queue (per
  // CLAUDE.md §9 — N1 forbids drafts on iMessage/SMS/WhatsApp/calls).
  // Drop the .eq('agent', ...) once additional agents start writing
  // approvals.
  const { data, error } = await supabase
    .from('approvals')
    .select(BELL_APPROVAL_SELECT)
    .eq('agent', 'email_triage')
    .eq('status', 'pending')
    .order('created_at', { ascending: false })
    .limit(5);
  if (error) throw error;
  return (data ?? []).map((row: Record<string, unknown>): BellApproval => {
    const email = (row.emails ?? null) as {
      from_email: string | null;
      from_name: string | null;
      subject: string | null;
      occurred_at: string | null;
    } | null;
    const draft = (row.draft_payload ?? {}) as { subject?: unknown };
    const subjectFromEmail = typeof email?.subject === 'string' ? email.subject : null;
    const subjectFromDraft = typeof draft.subject === 'string' ? draft.subject : null;
    return {
      id: String(row.id),
      subject: subjectFromEmail ?? subjectFromDraft ?? '(no subject)',
      fromLine: email?.from_name?.trim() || email?.from_email?.trim() || '',
      occurredAt: email?.occurred_at ?? String(row.created_at ?? ''),
    };
  });
}

async function fetchPendingApprovalCount(): Promise<number> {
  const { count, error } = await supabase
    .from('approvals')
    .select('id', { count: 'exact', head: true })
    .eq('agent', 'email_triage')
    .eq('status', 'pending');
  if (error) throw error;
  return count ?? 0;
}

// ============================================================================
// Latest digest query
// ============================================================================

type LatestDigest = {
  id: string;
  summary: string;
  windowEnd: string;
};

async function fetchLatestDigest(): Promise<LatestDigest | null> {
  const { data, error } = await supabase
    .from('autonomous_brain_log')
    .select('id, summary, window_end')
    .is('deleted_at', null)
    .order('window_end', { ascending: false })
    .limit(1);
  if (error) throw error;
  const row = (data ?? [])[0];
  if (!row) return null;
  return {
    id: String(row.id),
    summary: String(row.summary ?? ''),
    windowEnd: String(row.window_end ?? ''),
  };
}

// ============================================================================
// Stale/alert daemons (derived from cached heartbeats)
// ============================================================================

type ServiceIssue = {
  daemonName: string;
  status: HeartbeatStatus;
  stalenessLabel: string;
};

function selectServiceIssues(rows: HeartbeatRow[], now: Date): ServiceIssue[] {
  const issues: ServiceIssue[] = [];
  for (const row of rows) {
    if (!row.payload) continue;
    const cls = classifyHeartbeat(row.payload, now, { daemonName: row.daemon_name });
    // 'asleep' is a capture daemon on a laptop with the lid shut. It is not an
    // issue and must not drive the bell badge — that badge is what tells the
    // owner something needs him, and a nightly false one teaches him to ignore it.
    if (cls.status === 'alive' || cls.status === 'asleep') continue;
    issues.push({
      daemonName: row.daemon_name,
      status: cls.status,
      stalenessLabel: `${cls.staleness_seconds}s stale`,
    });
  }
  // Worst (alert) first, then by name for stable order.
  return issues.sort((a, b) => {
    if (a.status !== b.status) return a.status === 'alert' ? -1 : 1;
    return a.daemonName.localeCompare(b.daemonName);
  });
}

// ============================================================================
// Component
// ============================================================================

export default function NotificationsPopover(): JSX.Element {
  // popoverOpen gates the 5-row list query so we don't spend a network
  // round-trip every 10s on data nobody can see. The COUNT query stays
  // unconditional — it's what drives the truthful badge dot, so it has
  // to be live whether the popover is open or not. R1 reviewer S4.
  const [popoverOpen, setPopoverOpen] = useState(false);

  // `now` advances every minute so staleness re-classifies even when
  // the heartbeats cache hasn't refetched (the cache refresh cadence is
  // 30s on SystemStatusChip / Settings; this tick keeps the popover's
  // tier labels honest in between). Matches the SystemStatusChip
  // pattern. R2 reviewer N3.
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const approvalsQ = useQuery({
    queryKey: ['bell', 'approvals'],
    queryFn: fetchPendingApprovalsForBell,
    refetchInterval: 10_000,
    retry: 0,
    enabled: popoverOpen,
  });

  const approvalCountQ = useQuery({
    queryKey: ['bell', 'approval-count'],
    queryFn: fetchPendingApprovalCount,
    refetchInterval: 10_000,
    retry: 0,
  });

  const digestQ = useQuery({
    queryKey: ['bell', 'latest-digest'],
    queryFn: fetchLatestDigest,
    refetchInterval: 60_000,
    retry: 0,
    enabled: popoverOpen,
  });

  // Read-only access to the shared heartbeats cache.
  const heartbeatsQ = useQuery<HeartbeatRow[]>({
    queryKey: ['heartbeats'],
    queryFn: async () => [],
    enabled: false,
  });

  const issues = useMemo<ServiceIssue[]>(
    () => selectServiceIssues(heartbeatsQ.data ?? [], now),
    [heartbeatsQ.data, now],
  );

  const approvalCount = approvalCountQ.data ?? 0;
  const warningCount = issues.length;
  const showDot = approvalCount + warningCount > 0;

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={showDot ? 'Notifications (has updates)' : 'Notifications'}
          title="Notifications"
          className="relative p-2 rounded-lg hover:bg-secondary transition-colors"
        >
          <Bell className="w-4 h-4 text-muted-foreground" />
          {showDot && (
            <span
              className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-copper"
              aria-hidden
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-80 p-0">
        <div className="px-4 py-3 border-b">
          <h3 className="text-sm font-medium text-foreground">Notifications</h3>
          <p className="text-[11px] text-muted-foreground">
            Pending approvals, service issues, and today’s digest.
          </p>
        </div>

        {/* ============================ Approvals ============================ */}
        <Section
          icon={Mail}
          title={`Pending approvals${approvalCount > 0 ? ` (${approvalCount})` : ''}`}
        >
          {approvalsQ.isLoading ? (
            <p className="text-[11px] text-muted-foreground px-3 py-2">Loading…</p>
          ) : approvalsQ.isError ? (
            <p className="text-[11px] text-alert px-3 py-2">Couldn’t load approvals.</p>
          ) : (approvalsQ.data ?? []).length === 0 ? (
            <p className="text-[11px] text-muted-foreground px-3 py-2">
              Inbox is clear — no drafts waiting.
            </p>
          ) : (
            <ul className="max-h-56 overflow-y-auto">
              {approvalsQ.data!.map((a) => (
                <li key={a.id}>
                  <Link
                    to={`/inbox#approval=${a.id}`}
                    className="block px-3 py-2 hover:bg-secondary transition-colors"
                  >
                    <p className="text-[12px] font-medium text-foreground truncate">{a.subject}</p>
                    <p className="text-[10px] text-muted-foreground truncate">
                      {a.fromLine}
                      {a.occurredAt ? (
                        <>
                          {a.fromLine ? ' · ' : ''}
                          {formatDistanceToNowStrict(new Date(a.occurredAt), { addSuffix: true })}
                        </>
                      ) : null}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {approvalCount > 5 && (
            <Link
              to="/inbox"
              className="block px-3 py-2 text-[11px] text-forest hover:text-forest/80 border-t"
            >
              View all {approvalCount} in Inbox →
            </Link>
          )}
        </Section>

        {/* ============================ Service issues ============================ */}
        <Section
          icon={AlertCircle}
          title={`Service issues${warningCount > 0 ? ` (${warningCount})` : ''}`}
        >
          {warningCount === 0 ? (
            <p className="text-[11px] text-muted-foreground px-3 py-2">All daemons healthy.</p>
          ) : (
            <ul className="max-h-56 overflow-y-auto">
              {issues.slice(0, 5).map((issue) => (
                <li key={issue.daemonName}>
                  <Link
                    to="/agents"
                    className="flex items-center justify-between px-3 py-2 hover:bg-secondary transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="text-[12px] font-medium text-foreground truncate">
                        {humanizeDaemonName(issue.daemonName)}
                      </p>
                      <p className="text-[10px] text-muted-foreground truncate font-mono">
                        {issue.daemonName}
                      </p>
                    </div>
                    <span
                      className={`text-[10px] uppercase tracking-wide font-medium ${
                        issue.status === 'alert' ? 'text-alert' : 'text-attention'
                      }`}
                    >
                      {issue.status === 'alert' ? 'Stopped' : 'Stale'}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* ============================ Latest digest ============================ */}
        <Section icon={Lightbulb} title="Latest digest">
          {digestQ.isLoading ? (
            <p className="text-[11px] text-muted-foreground px-3 py-2">Loading…</p>
          ) : !digestQ.data ? (
            <p className="text-[11px] text-muted-foreground px-3 py-2">
              No digests yet. Brain runs nightly.
            </p>
          ) : (
            <Link to="/intel" className="block px-3 py-2 hover:bg-secondary transition-colors">
              <p className="text-[12px] text-foreground line-clamp-2">
                {digestQ.data.summary.split('\n')[0] || '(no summary)'}
              </p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                Window ending{' '}
                {formatDistanceToNowStrict(new Date(digestQ.data.windowEnd), { addSuffix: true })}
              </p>
            </Link>
          )}
        </Section>
      </PopoverContent>
    </Popover>
  );
}

function Section(props: {
  icon: LucideIcon;
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  const Icon = props.icon;
  return (
    <div className="border-b last:border-b-0">
      <div className="flex items-center gap-1.5 px-3 pt-3 pb-1">
        <Icon className="w-3 h-3 text-muted-foreground" aria-hidden />
        <h4 className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
          {props.title}
        </h4>
      </div>
      {props.children}
    </div>
  );
}

// Exported for unit testing.
export { selectServiceIssues };
